import React, { useState, useRef } from 'react';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { motion, useInView } from 'framer-motion';
import { useToast } from '../hooks/use-toast';

const Contact = () => {
  const { toast } = useToast();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const base = process.env.REACT_APP_API_URL || '';
    try {
      const res = await fetch(`${base}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          message: formData.message,
        }),
      });
      const data = await res.json().catch(() => ({}));

      if (data.code === 'SPAM_DETECTED') {
        toast({
          variant: 'destructive',
          title: '⚠️ Suspicious Submission Detected',
          description:
            'Our system flagged your message as potentially suspicious or fake (error 550). ' +
            'Please check your details and try again, or reach us directly at support@transactworld.com.',
        });
        return;
      }

      if (!res.ok) {
        throw new Error(data.error || 'Something went wrong.');
      }
      toast({
        title: 'Message Sent!',
        description: 'Thank you for contacting us. We will get back to you soon.',
      });
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Could not send',
        description: err.message || 'Please try again in a moment.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-12 sm:py-16 md:py-24 bg-white relative overflow-hidden">
      <div className="absolute -top-20 -right-20 w-96 h-96 bg-gradient-to-br from-purple-200/30 to-cyan-200/30 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-20 -left-20 w-96 h-96 bg-gradient-to-br from-pink-200/30 to-purple-200/30 rounded-full blur-3xl pointer-events-none" />

      <div ref={ref} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Title */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 sm:mb-16 md:mb-20"
        >
          <motion.h2
            initial={{ opacity: 0, scale: 0.8 }}
            animate={isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4 px-1"
            style={{ fontFamily: '"Space Grotesk", sans-serif' }}
          >
            GET IN{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 via-pink-600 to-cyan-600">
              TOUCH
            </span>
          </motion.h2>
          <motion.div
            initial={{ width: 0 }}
            animate={isInView ? { width: 96 } : { width: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="h-1.5 bg-gradient-to-r from-purple-500 via-pink-500 to-cyan-500 mx-auto rounded-full mb-4"
          ></motion.div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto text-justify hyphens-auto"
          >
            Have questions? We're here to help.
          </motion.p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-10 md:gap-16">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <h3
              className="text-3xl font-black text-gray-900 mb-8"
              style={{ fontFamily: '"Space Grotesk", sans-serif' }}
            >
              Contact Information
            </h3>

            <div className="space-y-6 mb-10">
              {[
                {
                  icon: Mail,
                  label: 'Email',
                  value: 'support@transactworld.com',
                  color: 'from-purple-500 to-pink-500',
                },
                {
                  icon: Phone,
                  label: 'Phone',
                  value: '+44 20 3129 7109',
                  color: 'from-cyan-500 to-blue-500',
                },
                {
                  icon: MapPin,
                  label: 'Address',
                  value:
                    '3 Thivon Street, Anna Court 8-9\nOffice 202, CY-1056\nNicosia, Cyprus',
                  color: 'from-pink-500 to-orange-500',
                },
              ].map((item, idx) => (
                <motion.div
                  key={item.label}
                  initial={{ opacity: 0, x: -20 }}
                  animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
                  transition={{ delay: 0.7 + idx * 0.1 }}
                  whileHover={{ x: 10, scale: 1.02 }}
                  className="flex items-start space-x-4 p-4 bg-gradient-to-r from-purple-50 to-cyan-50 rounded-2xl border border-purple-200/30"
                >
                  <motion.div
                    whileHover={{ rotate: 360, scale: 1.2 }}
                    transition={{ duration: 0.6 }}
                    className={`w-14 h-14 bg-gradient-to-br ${item.color} rounded-xl flex items-center justify-center shadow-lg flex-shrink-0`}
                  >
                    <item.icon className="w-7 h-7 text-white" />
                  </motion.div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">{item.label}</h4>
                    <p className="text-gray-600 whitespace-pre-line">{item.value}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Google Map Inserted */}
            <div id="map-section" className="section mb-10">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3261.5002965256213!2d33.37395431487005!3d35.16908118031687!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14de1768b5e46669%3A0xcf2277f5bc15447f!2sThivon%2C+Nicosia%2C+Cyprus!5e0!3m2!1sen!2sin!4v1475251898184"
                width="100%"
                title="Nicosia Cyprus Map"
                className="w-full h-[220px] sm:h-[280px] rounded-xl border-0"
                style={{ border: 0, minWidth: '200px' }}
                allowFullScreen=""
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 50 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="relative"
          >
            <motion.div
              whileHover={{ scale: 1.01 }}
              className="bg-gradient-to-br from-white to-purple-50/50 p-6 sm:p-8 md:p-10 rounded-2xl sm:rounded-3xl shadow-2xl border border-purple-200/50 relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-purple-400/20 to-cyan-400/20 rounded-full blur-3xl pointer-events-none" />

              <h3
                className="text-3xl font-black text-gray-900 mb-8 relative z-10"
                style={{ fontFamily: '"Space Grotesk", sans-serif' }}
              >
                Send us a Message
              </h3>

              <form onSubmit={handleSubmit} className="space-y-6 relative z-10">
                {[
                  {
                    name: 'name',
                    label: 'Full Name *',
                    type: 'text',
                    placeholder: 'John Doe',
                  },
                  {
                    name: 'email',
                    label: 'Email *',
                    type: 'email',
                    placeholder: 'john@example.com',
                  },
                  {
                    name: 'phone',
                    label: 'Phone',
                    type: 'tel',
                    placeholder: '+1 234 567 8900',
                  },
                ].map((field, idx) => (
                  <motion.div
                    key={field.name}
                    initial={{ opacity: 0, y: 20 }}
                    animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                    transition={{ delay: 0.8 + idx * 0.1 }}
                  >
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      {field.label}
                    </label>
                    <motion.input
                      whileFocus={{
                        scale: 1.02,
                        boxShadow:
                          '0 10px 30px rgba(139, 92, 246, 0.2)',
                      }}
                      type={field.type}
                      name={field.name}
                      value={formData[field.name]}
                      onChange={handleChange}
                      required={field.label.includes('*')}
                      className="w-full px-5 py-4 border-2 border-purple-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all bg-white/80 backdrop-blur-sm"
                      placeholder={field.placeholder}
                    />
                  </motion.div>
                ))}

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                  transition={{ delay: 1.1 }}
                >
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Message *
                  </label>
                  <motion.textarea
                    whileFocus={{
                      scale: 1.02,
                      boxShadow:
                        '0 10px 30px rgba(139, 92, 246, 0.2)',
                    }}
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="4"
                    className="w-full px-5 py-4 border-2 border-purple-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all resize-none bg-white/80 backdrop-blur-sm"
                    placeholder="Tell us how we can help you..."
                  ></motion.textarea>
                </motion.div>

                <motion.button
                  type="submit"
                  disabled={submitting}
                  whileHover={
                    submitting
                      ? {}
                      : {
                          scale: 1.05,
                          boxShadow:
                            '0 20px 40px rgba(139, 92, 246, 0.4)',
                        }
                  }
                  whileTap={submitting ? {} : { scale: 0.95 }}
                  className="w-full relative px-6 py-5 bg-gradient-to-r from-purple-600 via-pink-600 to-cyan-600 text-white rounded-xl font-bold shadow-xl overflow-hidden group disabled:opacity-60 disabled:pointer-events-none"
                >
                  <span className="relative z-10 flex items-center justify-center space-x-2">
                    <span>{submitting ? 'Sending…' : 'Send Message'}</span>
                    <Send className="w-5 h-5" />
                  </span>
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-cyan-600 via-purple-600 to-pink-600"
                    initial={{ x: '-100%' }}
                    whileHover={{ x: 0 }}
                    transition={{ duration: 0.3 }}
                  ></motion.div>
                </motion.button>
              </form>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;